/**
 * InvoiceDraftCardConnected — wrapper que conecta hooks de invoice outreach
 * al DraftCard shared. Billing Phase 2. Espejo de EstimateDraftCardConnected:
 * send + update + cancel + reply — sin skip/regenerate/followup/schedule.
 */
import { useTranslation } from 'react-i18next';
import { DraftCard } from '../outreach/DraftCard.js';
import type { DraftCardLabels, NormalizedDraft } from '../outreach/types.js';
import {
  useSendInvoiceDraft,
  useCancelInvoiceDraft,
  useUpdateInvoiceDraft,
  type InvoiceOutreachDraft,
} from '../../hooks/useInvoiceOutreach.js';

interface InvoiceDraftCardConnectedProps {
  draft: InvoiceOutreachDraft;
  invoiceId: number;
}

export function InvoiceDraftCardConnected({ draft, invoiceId }: InvoiceDraftCardConnectedProps) {
  const { t } = useTranslation();
  const send = useSendInvoiceDraft(invoiceId);
  const cancel = useCancelInvoiceDraft(invoiceId);
  const update = useUpdateInvoiceDraft(invoiceId);

  const normalized: NormalizedDraft = {
    id: draft.id,
    draftType: draft.draftType,
    subject: draft.subject,
    bodyHtml: draft.bodyHtml,
    bodyText: draft.bodyText,
    status: draft.status,
    sendError: draft.sendError,
    sentAt: draft.sentAt,
    createdAt: draft.createdAt,
    sequenceOrder: draft.sequenceOrder,
    sentMessageId: draft.sentMessageId,
  };

  const labels: DraftCardLabels = {
    initial: t('billing.outreach.draftType.invoiceReview'),
    followupN: t('billing.outreach.draftType.invoiceReview'),
    newEmail: t('billing.outreach.draftType.invoiceReview'),
    briefRequest: '',
    contentPlanReview: '',
    designReview: '',
    invoiceReview: t('billing.outreach.draftType.invoiceReview'),
    paymentReminder: t('billing.outreach.draftType.paymentReminder'),
    replyType: t('billing.outreach.draftType.reply'),
    editBtn: t('billing.outreach.editBtn'),
    editSave: t('billing.outreach.editSave'),
    editCancel: t('common.cancel'),
    sendBtn: t('billing.outreach.sendBtn'),
    sendingLabel: t('billing.outreach.sending'),
    sendNow: t('billing.outreach.sendBtn'),
    cancelBtn: t('billing.outreach.cancelConfirm'),
    skipBtn: '',
    retryBtn: t('billing.outreach.retryBtn'),
    expandBtn: t('billing.outreach.expandBtn'),
    collapseBtn: t('billing.outreach.collapseBtn'),
    copyBtn: t('billing.outreach.copyBtn'),
    copied: t('billing.outreach.copied'),
    replyBtn: t('billing.outreach.reply'),
    sendReplyBtn: t('billing.outreach.sendReplyBtn'),
    sentAgo: t('billing.outreach.sentAgo', { time: '{time}' }),
    scheduledForLabel: '',
    editSubjectLabel: t('billing.outreach.editSubjectLabel'),
    editBodyLabel: t('billing.outreach.editBodyLabel'),
    actionFailed: t('billing.outreach.actionFailed'),
    composerPlaceholder: t('billing.outreach.composing'),
    failedStatus: t('billing.outreach.status.failed'),
    cancelledStatus: t('billing.outreach.status.cancelled'),
  };

  return (
    <DraftCard
      draft={normalized}
      labels={labels}
      useRichTextEditor={false}
      onSend={() => send.mutate(draft.id)}
      onCancel={() => cancel.mutate({ draftId: draft.id })}
      onUpdate={({ subject, bodyHtml, bodyText }) =>
        update.mutate({ draftId: draft.id, subject, bodyHtml, bodyText })
      }
      sendState={{ isPending: send.isPending, isError: send.isError, error: send.error }}
      cancelState={{ isPending: cancel.isPending, isError: cancel.isError, error: cancel.error }}
      updateState={{ isPending: update.isPending, isError: update.isError, error: update.error }}
    />
  );
}
